import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Card, Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import PageHeader from "../../shared/PageHeader";
import ImagePreviewModal from "../../shared/ImagePreviewModal";
import FieldRenderer, { FieldSchema } from "../DynamicOrderForm/FieldRenderer";
import celebrationCakeSchema from "../../../formSchemas/celebration-cake.json";
import {
  createOrder,
  updateOrder,
  getOrderById,
} from "../../../services/OrderService";

interface SectionSchema {
  title: string;
  fields: FieldSchema[];
}

interface FormSchema {
  type: string;
  title: string;
  sections: SectionSchema[];
}

const computeField = (
  expression: string,
  data: Record<string, any>
): number => {
  try {
    const tokens = expression.split(/\s*([+-])\s*/);
    let result = parseFloat(data[tokens[0]] || "0") || 0;
    for (let i = 1; i < tokens.length; i += 2) {
      const operator = tokens[i];
      const fieldName = tokens[i + 1];
      const value = parseFloat(data[fieldName] || "0") || 0;
      if (operator === "+") result += value;
      else if (operator === "-") result -= value;
    }
    return Math.round(result * 100) / 100;
  } catch {
    return 0;
  }
};

const CelebrationCakeForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();

  const isPrintMode = location.pathname.includes("/orders/print/");
  const isEditMode = location.pathname.includes("/orders/edit/");
  const isViewMode = (!!id && !isEditMode) || isPrintMode;

  const schema = celebrationCakeSchema as FormSchema;

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [existingOrder, setExistingOrder] = useState<any>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if ((isViewMode || isEditMode) && id) {
          const order = await getOrderById(parseInt(id, 10));
          if (!order) {
            setLoading(false);
            return;
          }
          if (isEditMode) setExistingOrder(order);

          if (order.formData) {
            try {
              setFormData(JSON.parse(order.formData));
            } catch {
              setFormData({});
            }
          }
        } else {
          const initialData: Record<string, any> = {};
          schema.sections.forEach((section) => {
            section.fields.forEach((field) => {
              if (field.defaultValue !== undefined) {
                initialData[field.name] = field.defaultValue;
              } else if (field.type === "quantity") {
                initialData[field.name] = 1;
              } else {
                initialData[field.name] = "";
              }
            });
          });

          // Attach supplierId from navigation state
          const stateSupplierId = location.state?.supplierId;
          if (stateSupplierId) {
            initialData["supplierId"] = stateSupplierId;
          }

          try {
            const userData = JSON.parse(
              localStorage.getItem("client_user_data") || "{}"
            );
            if (userData?.clientId) {
              initialData["clientId"] = userData.clientId;
            }
            if (userData?.clientName) {
              initialData["branch"] = userData.clientName;
              initialData["collectFrom"] = userData.clientName;
            }
          } catch {}

          setFormData(initialData);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isViewMode, isEditMode]);

  useEffect(() => {
    const computedFields: FieldSchema[] = [];
    schema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.computed) computedFields.push(field);
      });
    });

    if (computedFields.length === 0) return;

    setFormData((prev) => {
      let hasChanges = false;
      const updated = { ...prev };
      computedFields.forEach((field) => {
        const computed = computeField(field.computed!, prev);
        if (updated[field.name] !== computed) {
          updated[field.name] = computed;
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });
  }, [formData]);

  // Convert a File to a base64 data URL string
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Build a clean copy of formData with files converted to base64
  const prepareFormDataJson = async (): Promise<Record<string, any>> => {
    const prepared = { ...formData };
    for (const section of schema.sections) {
      for (const field of section.fields) {
        if (field.type === "file" && prepared[field.name] instanceof File) {
          prepared[field.name] = await fileToBase64(prepared[field.name]);
        }
      }
    }
    return prepared;
  };

  const handleSubmit = async (e: React.FormEvent, saveAsDraft = false) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const preparedData = await prepareFormDataJson();

      if (isEditMode && existingOrder && id) {
        const updatePayload: Record<string, any> = {
          dateRequired: preparedData.requiredDate || existingOrder.dateRequired,
          total: preparedData.total || 0,
          formData: JSON.stringify(preparedData),
        };
        if (saveAsDraft) {
          updatePayload.status = "Draft";
        }
        const result = await updateOrder(parseInt(id, 10), updatePayload);
        if (result) {
          navigate("/orders");
        } else {
          setSubmitError("Failed to update order.");
        }
      } else {
        const orderPayload = {
          clientId: preparedData.clientId || 0,
          supplierId: preparedData.supplierId || 0,
          type: schema.type,
          dateRequired: preparedData.requiredDate || new Date().toISOString(),
          total: preparedData.total || 0,
          status: saveAsDraft ? "Draft" : "Pending",
          formData: JSON.stringify(preparedData),
        };

        const result = await createOrder(orderPayload as any);
        if (result) {
          navigate("/orders");
        } else {
          setSubmitError(
            saveAsDraft ? "Failed to save draft." : "Failed to create order."
          );
        }
      }
    } catch (error: any) {
      console.error(error);
      const message = error?.response?.data?.message || error?.message || "An error occurred while saving the order.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="container-fluid min-vh-100 bg-body-tertiary px-3 px-lg-4 py-4 d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
      </div>
    );

  return (
    <div className={`container-fluid min-vh-100 px-3 px-lg-4 py-4${isPrintMode ? " bg-white p-0" : " bg-body-tertiary"}`}>
      <div className="d-flex flex-column gap-3">
        {!isPrintMode && (
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
            <PageHeader title="Manage Orders" subTitle="Orders" />
            <div className="d-flex flex-column flex-sm-row gap-2 ms-lg-auto">
              <Button
                variant="light"
                size="sm"
                onClick={() => navigate("/orders")}
                className="border rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
              >
                <i className="bi bi-arrow-left me-1"></i>
                Back to Orders
              </Button>
            </div>
          </div>
        )}

        <Card className={`border-0 shadow-sm rounded-4${isPrintMode ? " shadow-none" : ""}`}>
          <Card.Body className="p-3 p-lg-4">
            <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
              <div className="rounded-4 p-2 p-lg-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning-emphasis">
                <i className="bi bi-cake2 fs-4"></i>
              </div>
              <div>
                <h4 className="mb-0 fw-semibold text-dark">
                  {isEditMode
                    ? `Edit: ${schema.title}`
                    : isViewMode && !isPrintMode
                    ? `View: ${schema.title}`
                    : schema.title}
                </h4>
                <small className="text-body-secondary">
                  {isEditMode
                    ? "Update the order details below"
                    : isViewMode
                    ? "Order details are shown below"
                    : "Fill in the details below to create a new celebration cake order"}
                </small>
              </div>
            </div>

            <Form onSubmit={handleSubmit}>
              {schema.sections.map((section, sectionIdx) => (
                <div key={sectionIdx} className={sectionIdx > 0 ? "mt-4 pt-3 border-top" : ""}>
                  <h6 className="fw-semibold text-uppercase text-secondary small mb-3">
                    {section.title}
                  </h6>
                  {section.fields.map((field, fieldIdx) => (
                    <Row key={fieldIdx} className="mb-3 align-items-center">
                      <Col xs={12} md={3} lg={2}>
                        <Form.Label className="small fw-semibold text-body-secondary mb-1 mb-md-0">
                          {field.label}
                        </Form.Label>
                      </Col>
                      <Col xs={12} md={9} lg={10}>
                        <FieldRenderer
                          field={field}
                          value={formData[field.name] ?? ""}
                          formData={formData}
                          setFormData={setFormData}
                          onShowProductModal={() => {}}
                          readOnly={isViewMode && !isEditMode}
                          onShowImagePreview={(src) => setPreviewImage(src)}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
              ))}

              {submitError && (!isViewMode || isEditMode) && (
                <Alert variant="danger" className="mb-3 mt-3">
                  {submitError}
                </Alert>
              )}

              <div className="mt-4 pt-3 border-top d-flex gap-2">
                {isEditMode && (
                  <>
                    <Button
                      type="submit"
                      variant="primary"
                      className="px-4 shadow-sm"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        "Update Order"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="px-4"
                      disabled={submitting}
                      onClick={(e) => handleSubmit(e as any, true)}
                    >
                      Save as Draft
                    </Button>
                  </>
                )}
                {!isViewMode && !isEditMode && (
                  <>
                    <Button
                      type="submit"
                      variant="primary"
                      className="px-4 shadow-sm"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        "Create Order"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="px-4"
                      disabled={submitting}
                      onClick={(e) => handleSubmit(e as any, true)}
                    >
                      Save as Draft
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="light"
                  className="border px-4"
                  onClick={() => navigate("/orders")}
                >
                  {isViewMode ? "Back" : "Close"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </div>
  );
};

export default CelebrationCakeForm;
